import type { SidebarGroup } from "../../domain/app-types.ts";
import { destinationKey, rememberDestination, toggleFavorite, type DestinationRef } from "./destinations.ts";
import { rememberRecentTask, toggleFavoriteTask, type RecentTask } from "./qol-preferences.ts";
import type { SidebarMenuState } from "./sidebar-interaction-controller.ts";

export interface NavigationStateInitial {
  expandedGroup: SidebarGroup | null;
  expandedSpaceId: string | null;
  favoriteDestinations: DestinationRef[];
  recentDestinations: DestinationRef[];
  onboardingDismissed: boolean;
  favoriteTaskIds: string[];
  recentTasks: RecentTask[];
}

export class NavigationState {
  expandedGroup: SidebarGroup | null;
  groupSelectionMade = false;
  expandedSpaceId: string | null;
  openMenu: SidebarMenuState | null = null;
  favoriteDestinations: DestinationRef[];
  recentDestinations: DestinationRef[];
  onboardingDismissed: boolean;
  favoriteTaskIds: string[];
  recentTasks: RecentTask[];

  constructor(initial: NavigationStateInitial) {
    this.expandedGroup = initial.expandedGroup;
    this.expandedSpaceId = initial.expandedSpaceId;
    this.favoriteDestinations = initial.favoriteDestinations;
    this.recentDestinations = initial.recentDestinations;
    this.onboardingDismissed = initial.onboardingDismissed;
    this.favoriteTaskIds = initial.favoriteTaskIds;
    this.recentTasks = initial.recentTasks;
  }

  toggleGroup(group: SidebarGroup, effectiveGroup: SidebarGroup | null): SidebarGroup | null {
    this.groupSelectionMade = true;
    this.expandedGroup = effectiveGroup === group ? null : group;
    return this.expandedGroup;
  }

  toggleSpace(id: string): string | null {
    this.expandedSpaceId = this.expandedSpaceId === id ? null : id;
    return this.expandedSpaceId;
  }

  toggleMenu(kind: SidebarMenuState["kind"], id: string): boolean {
    const wasOpen = this.openMenu?.kind === kind && this.openMenu.id === id;
    this.openMenu = wasOpen ? null : { kind, id };
    return !wasOpen;
  }

  toggleDestinationFavorite(destination: DestinationRef): boolean {
    this.favoriteDestinations = toggleFavorite(this.favoriteDestinations, destination);
    return this.isFavorite(destination);
  }

  isFavorite(destination: DestinationRef): boolean {
    const key = destinationKey(destination);
    return this.favoriteDestinations.some(item => destinationKey(item) === key);
  }

  rememberDestination(destination: DestinationRef): DestinationRef[] {
    this.recentDestinations = rememberDestination(this.recentDestinations, destination);
    return this.recentDestinations;
  }

  toggleTaskFavorite(id: string): boolean {
    this.favoriteTaskIds = toggleFavoriteTask(this.favoriteTaskIds, id);
    return this.favoriteTaskIds.includes(id);
  }

  removeTaskFavorite(id: string): string[] {
    this.favoriteTaskIds = this.favoriteTaskIds.filter(item => item !== id);
    return this.favoriteTaskIds;
  }

  rememberTask(task: RecentTask): RecentTask[] {
    this.recentTasks = rememberRecentTask(this.recentTasks, task);
    return this.recentTasks;
  }
}
